package br.com.texeratx.boardgametimeapp.domain.usecases.games

import br.com.texeratx.boardgametimeapp.data.repository.GameRepository
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailOwnerUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailScorePlayerUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailScoreStatsUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailStatsUI
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailUIModel
import br.com.texeratx.boardgametimeapp.ui.screens.games.detail.GameDetailWinnerUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchGameUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchListItemUI
import br.com.texeratx.boardgametimeapp.ui.screens.matches.model.MatchPlayerUI
import br.com.texeratx.boardgametimeapp.ui.utils.apiToUiDate
import kotlin.math.abs
import kotlin.math.roundToInt

class GetGameDetailUseCase(
    private val repository: GameRepository
) {
    suspend operator fun invoke(gameId: Long): Result<GameDetailUIModel> {
        return repository.getGameById(gameId).map { dto ->
            val matchDurations = dto.matches.map { it.duration }
            val allPlayersWithScores = dto.matches.flatMap { it.players }
            val winnerScores = dto.matches.mapNotNull { match ->
                match.players.maxByOrNull { it.points }?.points
            }

            val highestScore = allPlayersWithScores.maxOfOrNull { it.points }
            val lowestScore = allPlayersWithScores.minOfOrNull { it.points }

            val highestScorePlayers = highestScore
                ?.let { target ->
                    allPlayersWithScores
                        .filter { sameScore(it.points, target) }
                        .distinctBy { it.id }
                        .map { player ->
                            GameDetailScorePlayerUI(
                                id = player.id,
                                name = player.name
                            )
                        }
                        .sortedBy { it.name.lowercase() }
                }
                ?: emptyList()

            val lowestScorePlayers = lowestScore
                ?.let { target ->
                    allPlayersWithScores
                        .filter { sameScore(it.points, target) }
                        .distinctBy { it.id }
                        .map { player ->
                            GameDetailScorePlayerUI(
                                id = player.id,
                                name = player.name
                            )
                        }
                        .sortedBy { it.name.lowercase() }
                }
                ?: emptyList()

            val winners = dto.matches
                .mapNotNull { match ->
                    // Mantem a mesma regra da UI atual: em empate, considera o primeiro do ranking.
                    match.players.maxByOrNull { it.points }
                }
                .groupBy { it.id }
                .map { (_, winsByPlayer) ->
                    val winner = winsByPlayer.first()
                    GameDetailWinnerUI(
                        id = winner.id,
                        name = winner.name,
                        imageUrl = winner.imageUrl,
                        nickname = winner.nickname,
                        wins = winsByPlayer.size
                    )
                }
                .sortedWith(
                    compareByDescending<GameDetailWinnerUI> { it.wins }
                        .thenBy { it.name.lowercase() }
                )

            val game = dto.game
            GameDetailUIModel(
                id = game.id,
                name = game.name,
                imageUrl = game.imageUrl ?: game.thumbnailUrl,
                minPlayers = game.minNumberOfPlayers,
                maxPlayers = game.maxNumberOfPlayers,
                duration = game.gameDuration,
                categoryId = game.gameCategoryId,
                category = game.categoryName,
                modeId = game.gameModeId,
                mode = game.modeName,
                bggUrl = game.bggUrl,
                manualUrl = game.manualUrl,
                spotifyUrl = game.spotifyUrl,
                matchesPlayed = game.matchesPlayed.toInt(),
                status = game.status,
                clanId = game.clanId,
                owners = dto.owners.map { owner ->
                    GameDetailOwnerUI(
                        id = owner.id,
                        name = owner.name,
                        imageUrl = owner.imageUrl,
                        nickname = owner.nickname,
                        birthday = owner.birthday,
                        phone = owner.phone,
                        status = owner.status,
                        clanId = owner.clanId
                    )
                },
                winners = winners,
                matches = dto.matches.map { match ->
                    MatchListItemUI(
                        id = match.id,
                        date = match.date,
                        duration = match.duration,
                        game = MatchGameUI(
                            id = match.game.id,
                            name = match.game.name,
                            imageUrl = match.game.imageUrl
                        ),
                        players = match.players.map { player ->
                            MatchPlayerUI(
                                id = player.id,
                                name = player.name,
                                nickname = player.nickname,
                                imageUrl = player.imageUrl,
                                points = player.points
                            )
                        },
                        expansionIds = match.clanExpansionIds
                    )
                },
                stats = GameDetailStatsUI(
                    matchesPlayed = (dto.stats?.matchesPlayed ?: game.matchesPlayed).toInt(),
                    matchesPlayedCurrentYear = (dto.stats?.matchesPlayedCurrentYear ?: 0L).toInt(),
                    lastPlayedDate = dto.matches.maxByOrNull { it.date }?.date?.let { apiToUiDate(it) },
                    firstPlayedDate = dto.matches.minByOrNull { it.date }?.date?.let { apiToUiDate(it) },
                    averageDurationInMinutes = if (matchDurations.isNotEmpty()) {
                        matchDurations.average().roundToInt()
                    } else {
                        null
                    },
                    longestDurationInMinutes = matchDurations.maxOrNull(),
                    shortestDurationInMinutes = matchDurations.minOrNull()
                ),
                scoreStats = GameDetailScoreStatsUI(
                    highestScore = highestScore,
                    highestScorePlayers = highestScorePlayers,
                    winnerAverageScore = if (winnerScores.isNotEmpty()) {
                        winnerScores.average()
                    } else {
                        null
                    },
                    lowestScore = lowestScore,
                    lowestScorePlayers = lowestScorePlayers
                )
            )
        }
    }

    private fun sameScore(a: Double, b: Double): Boolean = abs(a - b) < 0.0000001
}
